/**
 * 学生端 H5 的规则(原型 student/mobile-h5「体现的规则与决定」):手机上能做什么、不能做什么,只在这里定,
 * 页面与组件按这里的函数判断(纯函数,单元测试覆盖)。
 *
 *  - 补充决定:编程题(积木 / 代码)在手机上只看题、只回放(看自己交过的),动手做用电脑;
 *  - 随堂练习 / 课后作业 / 补强复测是整组提交、整组算正确率(作业首次成绩还进榜):
 *    一组里只要有编程题,手机上就整组不能交 —— 否则编程题按没答算,正确率被拉低(★ 口径);
 *  - 基线 3:阶段测评与考试不上手机(页面给「请用电脑」说明);
 *  - 作品提交在手机上只看进度。
 */

/** 手机上不能动手做的题型:Python / C++ 代码题、Scratch 作品题 */
export const HANDS_ON_TYPES: readonly string[] = ['code_python', 'code_cpp', 'code_scratch'];

/** 这道题要在电脑上动手做(手机上只看题、看已交的) */
export function handsOn(type: string): boolean {
  return HANDS_ON_TYPES.includes(type);
}

/** 整组提交的一组题里有编程题 → 这一组在手机上不能交,要到电脑上做 */
export function groupNeedsDesktop(types: readonly string[]): boolean {
  return types.some(handsOn);
}

/** 一组题里有几道要到电脑上做(提示语用) */
export function handsOnCount(types: readonly string[]): number {
  return types.filter(handsOn).length;
}

/** 学生端外壳的当前栏目(StudentShell 的 active) */
export type ShellActive =
  | 'journey'
  | 'map'
  | 'level'
  | 'growth'
  | 'qbank'
  | 'create'
  | 'exam'
  | 'parent'
  | 'messages'
  | 'settings'
  | 'me'
  | 'none';

/** 手机底部四个页签(原型:地图 / 课程 / 成长 / 我的) */
export type H5Tab = 'map' | 'course' | 'growth' | 'me';
export const H5_TABS: readonly { key: H5Tab; label: string; icon: string }[] = [
  { key: 'map', label: '地图', icon: 'map' },
  { key: 'course', label: '课程', icon: 'book' },
  { key: 'growth', label: '成长', icon: 'chart' },
  { key: 'me', label: '我的', icon: 'user' },
];

/**
 * 当前栏目落在哪个页签上:成长地图 / 学习地图 → 地图;关卡学习、结算、补强 → 课程;
 * 测评与成长 → 成长;题库、考试、消息、设置、课表和「我的」页 → 我的(这些入口都收在「我的」里)。
 */
export function h5TabOf(active: ShellActive): H5Tab | null {
  switch (active) {
    case 'journey':
    case 'map':
      return 'map';
    case 'level':
      return 'course';
    case 'growth':
      return 'growth';
    case 'qbank':
    case 'create':
    case 'exam':
    case 'messages':
    case 'settings':
    case 'me':
    case 'none':
      return 'me';
    default:
      return null;
  }
}

/**
 * 登录 / 注册成功后去哪:带了 redirect 就回原处;没带的,电脑照旧回官网首页(一期行为不变),
 * 手机直接进入口分流(学生 → 成长地图)—— 官网是 1440 画板,不该是手机登录后的第一屏。
 */
export function afterLoginPath(redirect: unknown, phone: boolean, fallback = '/'): string {
  if (typeof redirect === 'string' && redirect.startsWith('/')) return redirect;
  return phone ? '/home' : fallback;
}

/** 「请用电脑」说明页讲的是哪件事(路由名 → 说明的种类) */
export type DesktopOnlyWhat = 'exam' | 'checkin' | 'assessment' | 'coding' | 'work';
export function desktopOnlyWhatOf(routeName: unknown): DesktopOnlyWhat {
  if (routeName === 'exam-checkin') return 'checkin';
  if (routeName === 'assessment') return 'assessment';
  return 'exam';
}

export const DESKTOP_ONLY_TEXT: Record<DesktopOnlyWhat, { title: string; why: string }> = {
  exam: {
    title: '考试要在电脑上进行',
    why: '考试要开摄像头和屏幕共享,开了第三视角监考的场次还要用手机当旁边的摄像头,所以答题只能在电脑上。',
  },
  checkin: {
    title: '检录要在电脑上进行',
    why: '检录要在考试用的那台电脑上做设备检测、人脸核验和屏幕共享;手机留着,开了第三视角的场次要用它扫码当摄像头。',
  },
  assessment: {
    title: '阶段测评请用电脑',
    why: '阶段测评限一次、记成绩、要一口气做完,手机上容易被电话和切屏打断;请在电脑上打开同一个地址作答。',
  },
  coding: {
    title: '编程题请用电脑',
    why: '积木和代码要在电脑上动手做;手机上可以先看题、看讲解,做过的可以看自己交的内容。',
  },
  work: {
    title: '作品请用电脑提交',
    why: '作品文件要在电脑上上传;手机上可以看提交进度和结果。',
  },
};
